import {
  importLegacyPersistedMapStore,
  importMapDocument,
  type ImportedMapDocument,
  type MapDocument,
} from "@west-marches/shared";

const DB_NAME = "west-marches-map";
const STORE_NAME = "sessions";
const SESSION_KEY = "autosave";
const LEGACY_LOCAL_STORAGE_KEY = "west-marches-map";

function canUseIndexedDb(): boolean {
  return typeof window !== "undefined" && "indexedDB" in window;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, 1);

    request.onerror = () => {
      reject(request.error ?? new Error("Failed to open IndexedDB."));
    };

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  handler: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);
    const request = handler(store);

    request.onerror = () => {
      reject(request.error ?? new Error("IndexedDB request failed."));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    transaction.oncomplete = () => {
      db.close();
    };

    transaction.onerror = () => {
      reject(transaction.error ?? new Error("IndexedDB transaction failed."));
      db.close();
    };
  });
}

export async function loadMapSession(): Promise<ImportedMapDocument | null> {
  if (!canUseIndexedDb()) {
    return null;
  }

  const stored = await withStore<unknown | undefined>("readonly", (store) =>
    store.get(SESSION_KEY),
  );
  return stored ? importMapDocument(stored) : null;
}

export async function saveMapSession(document: MapDocument): Promise<void> {
  if (!canUseIndexedDb()) {
    return;
  }

  await withStore<IDBValidKey>("readwrite", (store) => store.put(document, SESSION_KEY));
}

export async function clearMapSession(): Promise<void> {
  if (!canUseIndexedDb()) {
    return;
  }

  await withStore<undefined>("readwrite", (store) => store.delete(SESSION_KEY));
}

export function loadLegacyMapSession(): ImportedMapDocument | null {
  if (typeof window === "undefined") {
    return null;
  }

  const raw = window.localStorage.getItem(LEGACY_LOCAL_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    return importLegacyPersistedMapStore(parsed);
  } catch {
    return null;
  }
}

export function clearLegacyMapSession(): void {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(LEGACY_LOCAL_STORAGE_KEY);
}
